import { useProduttore, useProduttoriList } from "../../hooks/produttori"
import { useDonatore, useDonatoriList } from "../../hooks/donatori"
import { usePersona, usePersoneList } from "../../hooks/persone"
import { useLuoghiList, useLuogo } from "../../hooks/luoghi"
import { useEntiList, useEnte } from "../../hooks/enti"
import { useFondiList, useFondo } from "../../hooks/fondi"
import { useSupportiList, useSupporto } from "../../hooks/supporti"
import { useTag, useTagsList } from "../../hooks/tags"
import { useAssemblaggiList, useAssemblaggio } from "../../hooks/assemblaggi"
import { useMemo, useState } from "react"
import {
  Produttore,
  Donatore,
  Persona,
  Luogo,
  Ente,
  Vocabolario,
  Tag,
  Fondo,
  Formato,
  Supporto,
  Assemblaggio,
} from "../../types"
import { ForeignAutoComplete, ForeignMultiAutoComplete } from "./formsHelpers"
import { useVocabolariList, useVocabolario } from "../../hooks/vocabolari"
import { useFormatiList, useFormato } from "../../hooks/formati"
import { joinNomeCognome } from "../../utils/text"

type ACProps<C> = {
  value: number | undefined
  onChange: (value: number | null) => void
  onBlur?: () => void
  error?: string
  touched?: boolean
  submitCount?: number
  variant?: "standard" | "outlined" | "filled" | undefined
  margin?: "none" | "dense" | "normal" | undefined
  size?: "small" | "medium" | undefined
  fullWidth?: boolean
  label?: string
  FormComponent?: React.ComponentType<any>
  formComponentProps?: Record<string, any>
  create?: (data: any) => Promise<C>
  initialCreate?: (search: string) => Partial<C>
  required?: boolean
}

export function ACPersona({ value, onChange, ...props }: ACProps<Persona>) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = usePersoneList({ search })
  const { data: selectedOption, isLoading: selectedOptionLoading } = usePersona(value || undefined)

  return (
    <ForeignAutoComplete<Persona>
      value={value}
      onChange={onChange}
      search={search}
      setSearch={setSearch}
      options={options?.results || []}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      selectedOption={selectedOption}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Persona) => option.nome}
      {...props}
    />
  )
}

export function ACProduttore({ value, onChange, size = "small", ...props }: ACProps<Produttore>) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = useProduttoriList({ search })
  const { data: selectedOption, isLoading: selectedOptionLoading } = useProduttore(value)

  return (
    <ForeignAutoComplete<Produttore>
      value={value}
      onChange={onChange}
      search={search}
      setSearch={setSearch}
      options={options?.results || []}
      size={size}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      selectedOption={selectedOption}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Produttore) => joinNomeCognome(option.nome, option.cognome)}
      {...props}
    />
  )
}

export function ACFondo({ value, onChange, size = "small", ...props }: ACProps<Fondo>) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = useFondiList({ search })
  const { data: selectedOption, isLoading: selectedOptionLoading } = useFondo(value)

  return (
    <ForeignAutoComplete<Fondo>
      value={value}
      onChange={onChange}
      search={search}
      setSearch={setSearch}
      options={options?.results || []}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      size={size}
      selectedOption={selectedOption}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Fondo) => option.segnatura}
      {...props}
    />
  )
}

export function ACFormato({ value, onChange, size = "small", ...props }: ACProps<Formato>) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = useFormatiList({ search })
  const { data: selectedOption, isLoading: selectedOptionLoading } = useFormato(value)

  return (
    <ForeignAutoComplete<Formato>
      value={value}
      onChange={onChange}
      search={search}
      setSearch={setSearch}
      options={options?.results || []}
      size={size}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      selectedOption={selectedOption}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Formato) => option.nome}
      {...props}
    />
  )
}

export function ACDonatore({ value, onChange, size = "small", ...props }: ACProps<Donatore>) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = useDonatoriList({ search })
  const { data: selectedOption, isLoading: selectedOptionLoading } = useDonatore(value)

  return (
    <ForeignAutoComplete<Donatore>
      value={value}
      onChange={onChange}
      search={search}
      setSearch={setSearch}
      options={options?.results || []}
      size={size}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      selectedOption={selectedOption}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Donatore) => joinNomeCognome(option.nome, option.cognome)}
      {...props}
    />
  )
}

export function ACEnte({ value, onChange, size = "small", ...props }: ACProps<Ente>) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = useEntiList({ search })
  const { data: selectedOption, isLoading: selectedOptionLoading } = useEnte(value)

  return (
    <ForeignAutoComplete<Ente>
      value={value}
      onChange={onChange}
      search={search}
      setSearch={setSearch}
      options={options?.results || []}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      size={size}
      selectedOption={selectedOption}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Ente) => option.nome}
      {...props}
    />
  )
}

export function ACLuogo({ value, onChange, size = "small", ...props }: ACProps<Luogo>) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = useLuoghiList({ search })
  const { data: selectedOption, isLoading: selectedOptionLoading } = useLuogo(value)

  return (
    <ForeignAutoComplete<Luogo>
      value={value}
      onChange={onChange}
      search={search}
      setSearch={setSearch}
      options={options?.results || []}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      size={size}
      selectedOption={selectedOption}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Luogo) => `${option.nome} (${option.livello_geografico})`}
      {...props}
    />
  )
}

export function ACSupporto({ value, onChange, size = "small", ...props }: ACProps<Supporto>) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = useSupportiList({ search })
  const { data: selectedOption, isLoading: selectedOptionLoading } = useSupporto(value)

  return (
    <ForeignAutoComplete<Supporto>
      value={value}
      onChange={onChange}
      search={search}
      setSearch={setSearch}
      options={options?.results || []}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      selectedOption={selectedOption}
      size={size}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Supporto) => `${option.segnatura}`}
      {...props}
    />
  )
}

export function ACAssemblaggio({ value, onChange, size = "small", ...props }: ACProps<Assemblaggio>) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = useAssemblaggiList({ search })
  const { data: selectedOption, isLoading: selectedOptionLoading } = useAssemblaggio(value)

  return (
    <ForeignAutoComplete<Assemblaggio>
      value={value}
      onChange={onChange}
      search={search}
      setSearch={setSearch}
      options={options?.results || []}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      selectedOption={selectedOption}
      size={size}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Assemblaggio) => option.nome}
      {...props}
    />
  )
}

export function ACAssemblaggioFormik({
  field,
  size = "small",
  form: { touched, errors, submitCount, setFieldValue, setFieldTouched },
  ...props
}: ACProps<Assemblaggio> & { field: any; form: any }) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = useAssemblaggiList({ search })
  const { data: selectedOption, isLoading: selectedOptionLoading } = useAssemblaggio(field.value)

  return (
    <ForeignAutoComplete<Assemblaggio>
      search={search}
      setSearch={setSearch}
      options={options?.results || []}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      size={size}
      selectedOption={selectedOption}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Assemblaggio) => option.nome}
      {...props}
      value={field.value}
      onChange={(v) => setFieldValue(field.name, v)}
      onBlur={() => setFieldTouched(field.name, true)}
      touched={touched[field.name]}
      submitCount={submitCount}
      error={errors[field.name]}
    />
  )
}

export function ACVocabolario({
  value,
  onChange,
  vocabolario,
  size = "small",
  ...props
}: ACProps<Vocabolario> & { vocabolario: string }) {
  const [search, setSearch] = useState("")
  const {
    data: options,
    refetch: refetchOptions,
    isLoading: optionsLoading,
  } = useVocabolariList({ search, vocabolario })
  const { data: selectedOption, isLoading: selectedOptionLoading } = useVocabolario(value || undefined)

  return (
    <ForeignAutoComplete<Vocabolario>
      value={value}
      onChange={onChange}
      search={search}
      setSearch={setSearch}
      options={options?.results || []}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      size={size}
      selectedOption={selectedOption}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Vocabolario) => option.termine}
      {...props}
    />
  )
}

export function ACVocabolarioFormik({
  vocabolario,
  field,
  size = "small",
  form: { touched, errors, submitCount, setFieldValue, setFieldTouched },
  ...props
}: ACProps<Vocabolario> & { vocabolario: string; field: any; form: any }) {
  const [search, setSearch] = useState("")
  const {
    data: options,
    refetch: refetchOptions,
    isLoading: optionsLoading,
  } = useVocabolariList({ search, vocabolario })
  const { data: selectedOption, isLoading: selectedOptionLoading } = useVocabolario(field.value || undefined)

  return (
    <ForeignAutoComplete<Vocabolario>
      search={search}
      setSearch={setSearch}
      size={size}
      options={options?.results || []}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      selectedOption={selectedOption}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Vocabolario) => option.termine}
      {...props}
      value={field.value}
      onChange={(v) => setFieldValue(field.name, v)}
      onBlur={() => setFieldTouched(field.name, true)}
      touched={touched[field.name]}
      submitCount={submitCount}
      error={errors[field.name]}
    />
  )
}

type ACMultiProps<C> = {
  value: number[]
  onChange: (value: number[]) => void
  onBlur?: () => void
  error?: string
  touched?: boolean
  submitCount?: number
  variant?: "standard" | "outlined" | "filled" | undefined
  margin?: "none" | "dense" | "normal" | undefined
  size?: "small" | "medium" | undefined
  fullWidth?: boolean
  label?: string
  FormComponent?: React.ComponentType<any>
  formComponentProps?: Record<string, any>
  create?: (data: any) => Promise<C>
  initialCreate?: (search: string) => Partial<C>
  excludeId?: number
}

export function ACPersonaMulti({ value, onChange, size = "small", ...props }: ACMultiProps<Persona>) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = usePersoneList({ search })

  const valuesFilter = useMemo(() => {
    return (value || []).map((v) => v.toString()).join(",")
  }, [value])
  const { data: selectedItems, isLoading: selectedOptionLoading } = usePersoneList({
    id__in: valuesFilter,
  })
  const selectedOptions = useMemo(() => {
    return value && value.length ? selectedItems?.results || [] : []
  }, [selectedItems, value])

  return (
    <ForeignMultiAutoComplete<Persona>
      value={value}
      onChange={onChange}
      search={search}
      size={size}
      setSearch={setSearch}
      options={options?.results || []}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      selectedOptions={selectedOptions}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Persona) => option.nome}
      {...props}
    />
  )
}

export function ACLuogoMulti({ value, onChange, size = "small", ...props }: ACMultiProps<Luogo>) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = useLuoghiList({ search })
  const valuesFilter = useMemo(() => {
    return (value || []).map((v) => v.toString()).join(",")
  }, [value])

  const { data: selectedItems, isLoading: selectedOptionLoading } = useLuoghiList({
    id__in: valuesFilter,
  })
  const selectedOptions = useMemo(() => {
    return value && value.length ? selectedItems?.results || [] : []
  }, [selectedItems, value])

  return (
    <ForeignMultiAutoComplete<Luogo>
      value={value}
      onChange={onChange}
      search={search}
      size={size}
      setSearch={setSearch}
      options={options?.results || []}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      selectedOptions={selectedOptions}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Luogo) => option.nome}
      {...props}
    />
  )
}

export function ACEnteMulti({ value, onChange, size = "small", ...props }: ACMultiProps<Ente>) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = useEntiList({ search })
  const valuesFilter = useMemo(() => {
    return (value || []).map((v) => v.toString()).join(",")
  }, [value])
  const { data: selectedItems, isLoading: selectedOptionLoading } = useEntiList({
    id__in: valuesFilter,
  })
  const selectedOptions = useMemo(() => {
    return value && value.length ? selectedItems?.results || [] : []
  }, [selectedItems, value])

  return (
    <ForeignMultiAutoComplete<Ente>
      value={value}
      onChange={onChange}
      search={search}
      setSearch={setSearch}
      size={size}
      options={options?.results || []}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      selectedOptions={selectedOptions}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Ente) => option.nome}
      {...props}
    />
  )
}

export function ACTag({ value, onChange, size = "small", ...props }: ACProps<Tag>) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = useTagsList({ search })
  const { data: selectedOption, isLoading: selectedOptionLoading } = useTag(value)

  return (
    <ForeignAutoComplete<Tag>
      value={value}
      onChange={onChange}
      search={search}
      setSearch={setSearch}
      options={options?.results || []}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      size={size}
      selectedOption={selectedOption}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Tag) => option.nome_it}
      {...props}
    />
  )
}

export function ACTagMulti({ value, onChange, size = "small", excludeId, ...props }: ACMultiProps<Tag>) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = useTagsList({ search })
  const valuesFilter = useMemo(() => {
    return (value || []).map((v) => v.toString()).join(",")
  }, [value])
  const { data: selectedItems, isLoading: selectedOptionLoading } = useTagsList({
    id__in: valuesFilter,
  })
  const selectedOptions = useMemo(() => {
    return value && value.length ? selectedItems?.results || [] : []
  }, [selectedItems, value])

  return (
    <ForeignMultiAutoComplete<Tag>
      value={value}
      onChange={onChange}
      search={search}
      setSearch={setSearch}
      options={options?.results.filter((option) => (excludeId ? option.id !== excludeId : true)) || []}
      optionsLoading={optionsLoading}
      size={size}
      refetchOptions={refetchOptions}
      selectedOptions={selectedOptions}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Tag) => option.nome}
      {...props}
    />
  )
}

type ACVocabolarioMultipProps = ACMultiProps<Vocabolario> & {
  vocabolario: string
}

export function ACVocabolarioMulti({
  value,
  vocabolario,
  size = "small",
  onChange,
  excludeId,
  ...props
}: ACVocabolarioMultipProps) {
  const [search, setSearch] = useState("")
  const {
    data: options,
    refetch: refetchOptions,
    isLoading: optionsLoading,
  } = useVocabolariList({ vocabolario, search })
  const valuesFilter = useMemo(() => {
    return (value || []).map((v) => v.toString()).join(",")
  }, [value])
  const { data: selectedItems, isLoading: selectedOptionLoading } = useVocabolariList({
    vocabolario,
    id__in: valuesFilter,
  })
  const selectedOptions = useMemo(() => {
    return value && value.length ? selectedItems?.results || [] : []
  }, [selectedItems, value])

  return (
    <ForeignMultiAutoComplete<Vocabolario>
      value={value}
      onChange={onChange}
      size={size}
      search={search}
      setSearch={setSearch}
      options={options?.results.filter((option) => (excludeId ? option.id !== excludeId : true)) || []}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      selectedOptions={selectedOptions}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Vocabolario) => option.termine}
      {...props}
    />
  )
}

export function ACSupportoMulti({ value, onChange, size = "small", ...props }: ACMultiProps<Supporto>) {
  const [search, setSearch] = useState("")
  const { data: options, refetch: refetchOptions, isLoading: optionsLoading } = useSupportiList({ search })

  const valuesFilter = useMemo(() => {
    return (value || []).map((v) => v.toString()).join(",")
  }, [value])
  const { data: selectedItems, isLoading: selectedOptionLoading } = useSupportiList({
    id__in: valuesFilter,
  })
  const selectedOptions = useMemo(() => {
    return value && value.length ? selectedItems?.results || [] : []
  }, [selectedItems, value])

  return (
    <ForeignMultiAutoComplete<Supporto>
      value={value}
      onChange={onChange}
      search={search}
      size={size}
      setSearch={setSearch}
      options={options?.results || []}
      optionsLoading={optionsLoading}
      refetchOptions={refetchOptions}
      selectedOptions={selectedOptions}
      selectedOptionLoading={selectedOptionLoading}
      getOptionLabel={(option: Supporto) => option.segnatura}
      {...props}
    />
  )
}
